import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLibraryMovies, useLibrarySeries } from './useLibrary'
import { useDevices } from '../devices/useDevices'
import { getEffectiveTmdbApiKey, useUserSettings } from '../settings/useUserSettings'
import { MovieGrid } from './MovieGrid'
import { SeriesGrid } from './SeriesGrid'
import { PlayerModal } from '../play/PlayerModal'
import { SearchOverlay } from '../search/SearchOverlay'
import { WatchlistsTab } from '../watchlists/WatchlistsTab'
import { PersonModal } from '../person/PersonModal'
import { MovieDetailModal } from '../detail/MovieDetailModal'
import { SeriesDetailModal } from '../detail/SeriesDetailModal'
import { SettingsSheet } from '../settings/SettingsSheet'
import { AccountMenu } from '../navigation/AccountMenu'
import { Logo } from '../ui'
import {
  DEFAULT_FILTERS,
  DEFAULT_MOVIE_SORT,
  DEFAULT_SERIES_SORT,
  isFiltersDirty,
  useFilteredSortedMovies,
  useFilteredSortedSeries,
  type LibraryDetailsEntry,
  type LibraryFilters,
  type MovieSortKey,
  type SeriesSortKey,
} from './useFilterSort'
import { LibrarySearchFilterBar } from './LibrarySearchFilterBar'
import { HomeView } from '../home/HomeView'
import { AwardsView, type AwardsJumpTarget } from '../awards/AwardsView'
import { fetchMovieDetails, fetchSeriesDetails } from '../tmdb/tmdb'
import { useBackButtonClose } from '../hooks'

type Tab = 'home' | 'movies' | 'series' | 'watchlists' | 'awards'

interface PlayingState {
  tmdbId: number
  type: 'movie' | 'tv'
  season?: number
  episode?: number
}

export function LibraryView() {
  // user is no longer read here directly — AccountMenu pulls its own
  // identity from useAuth and the header no longer displays the email
  // next to the settings button (AccountMenu shows it inside the
  // dropdown now, so the redundant inline span was removed).
  const { settings } = useUserSettings()
  const devices = useDevices()
  const { entries: movies, loading: moviesLoading } = useLibraryMovies()
  const { entries: series, loading: seriesLoading } = useLibrarySeries()

  const [tab, setTab] = useState<Tab>('home')
  const [playing, setPlaying] = useState<PlayingState | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [personId, setPersonId] = useState<number | null>(null)
  const [detailMovie, setDetailMovie] = useState<number | null>(null)
  const [detailSeries, setDetailSeries] = useState<number | null>(null)
  // Jump target forwarded from the detail modals' compact awards list.
  // `nonce` bumps on every click so repeat jumps to the same ceremony
  // re-trigger the effect in AwardsView.
  const [awardsJump, setAwardsJump] = useState<AwardsJumpTarget | null>(null)

  // Browser back button closes the topmost overlay/modal instead of
  // leaving the site. Each layer pushes its own history entry so a
  // nested stack (e.g. detail → person → lightbox) unwinds LIFO as
  // the user presses back. See hooks/useBackButtonClose.ts.
  useBackButtonClose(searchOpen, () => setSearchOpen(false))
  useBackButtonClose(settingsOpen, () => setSettingsOpen(false))
  useBackButtonClose(personId != null, () => setPersonId(null))
  useBackButtonClose(detailMovie != null, () => setDetailMovie(null))
  useBackButtonClose(detailSeries != null, () => setDetailSeries(null))
  useBackButtonClose(playing != null, () => setPlaying(null))

  const [filters, setFilters] = useState<LibraryFilters>(DEFAULT_FILTERS)
  const [movieSort, setMovieSort] = useState<MovieSortKey>(DEFAULT_MOVIE_SORT)
  const [seriesSort, setSeriesSort] = useState<SeriesSortKey>(DEFAULT_SERIES_SORT)

  // Background-resolved index of title / year / rating / runtime for
  // every library entry. Feeds both the in-library search and the
  // TMDB-derived sort keys (year / TMDB rating / runtime). Falls back to
  // the bundled developer key when the user hasn't supplied one so the
  // grid is never blank in default builds.
  const effectiveTmdbKey = getEffectiveTmdbApiKey(settings) ?? undefined
  const movieDetailsIndex = useLibraryDetailsIndex(
    movies.map((m) => m.tmdbId),
    'movie',
    effectiveTmdbKey,
    settings?.tmdbPreferredLanguage ?? undefined
  )
  const seriesDetailsIndex = useLibraryDetailsIndex(
    series.map((s) => s.tmdbId),
    'tv',
    effectiveTmdbKey,
    settings?.tmdbPreferredLanguage ?? undefined
  )

  const filteredMovies = useFilteredSortedMovies(movies, movieSort, filters, movieDetailsIndex)
  const filteredSeries = useFilteredSortedSeries(series, seriesSort, filters, seriesDetailsIndex)

  // Only flag "no TMDB key" when there is genuinely no key at all — the
  // bundled fallback covers the common case where the user never opened
  // settings, so we should not nag them.
  const noTmdbKey = settings && !effectiveTmdbKey

  const handleOpenDetail = (tmdbId: number, type: 'movie' | 'tv') => {
    if (type === 'movie') {
      setDetailMovie(tmdbId)
    } else {
      setDetailSeries(tmdbId)
    }
  }

  /**
   * Closes the currently-open detail modal, switches to the Awards
   * tab and forwards the target ceremony to `AwardsView`. Called from
   * the compact Awards section in MovieDetailModal / SeriesDetailModal.
   */
  const handleJumpToAwards = (
    bodyWikidataId: string,
    editionId: string
  ) => {
    setDetailMovie(null)
    setDetailSeries(null)
    setTab('awards')
    setAwardsJump({
      bodyWikidataId,
      editionId,
      nonce: Date.now(),
    })
    // Scroll back to the top so the Awards header is visible; the
    // detail modal previously had its own scroll position.
    window.scrollTo({ top: 0, behavior: 'auto' })
  }

  const handlePlay = (tmdbId: number, type: 'movie' | 'tv') => {
    setPlaying({ tmdbId, type })
  }

  const handlePlayEpisode = (tmdbId: number, season: number, episode: number) => {
    setPlaying({ tmdbId, type: 'tv', season, episode })
  }

  const filtersDirty =
    isFiltersDirty(filters) ||
    movieSort !== DEFAULT_MOVIE_SORT ||
    seriesSort !== DEFAULT_SERIES_SORT

  return (
    <div className="min-h-dvh bg-bg-primary text-text-primary">
      <header className="sticky top-0 z-30 border-b border-border-primary bg-bg-primary/90 backdrop-blur">
        <div className="mx-auto flex max-w-screen-2xl items-center gap-2 px-3 py-2 sm:px-4 sm:py-3">
          {/* Left: Govu logo. Kept compact on phones so the right-side
              action cluster always has breathing room. Links to /
              even though we're already there, matching the usual
              "logo == home" pattern users expect from web apps. */}
          <Link to="/" aria-label="Govu — home" className="flex shrink-0 items-center">
            <Logo className="h-7 w-auto sm:h-8" />
          </Link>

          <div className="flex-1" />

          {/* Right: single action cluster. Icon-only on phones (no
              label text) so all five items fit on a narrow viewport
              without the AccountMenu pushing the Settings button off
              screen. Labels re-appear at sm: and up. AccountMenu is
              rendered inline here; the old floating-fixed version
              used to overlap this row on mobile. */}
          <nav className="flex items-center gap-1.5 text-xs sm:gap-2">
            <Link
              to="/download"
              aria-label="Download desktop app"
              className="flex items-center gap-1.5 rounded-md border border-accent-primary/50 bg-accent-primary/10 px-2.5 py-1.5 font-semibold text-accent-primary transition-colors hover:bg-accent-primary/20 sm:px-3"
            >
              <DownloadIcon />
              <span className="hidden sm:inline">Desktop app</span>
            </Link>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="flex items-center gap-1.5 rounded-md bg-bg-secondary p-2 text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary sm:px-3 sm:py-1.5"
            >
              <SearchIcon />
              <span className="hidden sm:inline">Search</span>
            </button>
            <button
              type="button"
              onClick={() => setSettingsOpen(true)}
              aria-label="Settings"
              className="rounded-md bg-bg-secondary p-2 text-text-secondary transition-colors hover:bg-bg-tertiary hover:text-text-primary"
            >
              <SettingsIcon />
            </button>
            <AccountMenu />
          </nav>
        </div>
        <div className="mx-auto flex max-w-screen-2xl gap-1 overflow-x-auto px-4 pb-2 scrollbar-hide">
          <TabButton active={tab === 'home'} onClick={() => setTab('home')}>
            Home
          </TabButton>
          <TabButton active={tab === 'movies'} onClick={() => setTab('movies')}>
            Movies
          </TabButton>
          <TabButton active={tab === 'series'} onClick={() => setTab('series')}>
            Series
          </TabButton>
          <TabButton active={tab === 'watchlists'} onClick={() => setTab('watchlists')}>
            Watchlists
          </TabButton>
          <TabButton active={tab === 'awards'} onClick={() => setTab('awards')}>
            Awards
          </TabButton>
        </div>
      </header>

      <main className="mx-auto max-w-screen-2xl pb-6">
        {noTmdbKey && (
          <div className="mx-4 mt-4 rounded-md border border-accent-gold/30 bg-accent-gold/10 p-4 text-sm text-accent-gold">
            <p className="mb-1 font-medium">Posters and titles are missing</p>
            <p className="text-accent-gold/80">
              Add a TMDB API key in Settings → TMDB API key. It works for both
              this PWA and the desktop app once saved.
            </p>
          </div>
        )}

        {tab === 'home' && (
          <HomeView
            onOpenSearch={() => setSearchOpen(true)}
            onOpenDetail={handleOpenDetail}
            onPersonOpen={(id) => setPersonId(id)}
          />
        )}

        {tab === 'movies' && (
          <div className="px-4 pt-6">
            <LibrarySearchFilterBar
              scope="movies"
              filters={filters}
              onFiltersChange={setFilters}
              movieSort={movieSort}
              onMovieSortChange={setMovieSort}
              seriesSort={seriesSort}
              onSeriesSortChange={setSeriesSort}
              detailsIndex={movieDetailsIndex}
            />
            <MovieGrid
              entries={filteredMovies}
              devices={devices}
              loading={moviesLoading}
              emptyMessage={
                filtersDirty
                  ? 'No movies match the current filters.'
                  : 'No movies in your library yet.'
              }
              onOpenDetail={(id) => handleOpenDetail(id, 'movie')}
              keyboardEnabled={
                !searchOpen &&
                !settingsOpen &&
                detailMovie == null &&
                detailSeries == null &&
                personId == null &&
                playing == null
              }
            />
          </div>
        )}

        {tab === 'series' && (
          <div className="px-4 pt-6">
            <LibrarySearchFilterBar
              scope="series"
              filters={filters}
              onFiltersChange={setFilters}
              movieSort={movieSort}
              onMovieSortChange={setMovieSort}
              seriesSort={seriesSort}
              onSeriesSortChange={setSeriesSort}
              detailsIndex={seriesDetailsIndex}
            />
            <SeriesGrid
              entries={filteredSeries}
              devices={devices}
              loading={seriesLoading}
              emptyMessage={
                filtersDirty
                  ? 'No series match the current filters.'
                  : 'No series in your library yet.'
              }
              onOpenDetail={(id) => handleOpenDetail(id, 'tv')}
              keyboardEnabled={
                !searchOpen &&
                !settingsOpen &&
                detailMovie == null &&
                detailSeries == null &&
                personId == null &&
                playing == null
              }
            />
          </div>
        )}

        {tab === 'awards' && (
          <AwardsView
            onOpenDetail={handleOpenDetail}
            onPersonOpen={(id) => setPersonId(id)}
            jumpTarget={awardsJump}
          />
        )}

        {tab === 'watchlists' && (
          <div className="px-4 pt-6">
            <WatchlistsTab onOpenDetail={handleOpenDetail} />
          </div>
        )}
      </main>

      <PlayerModal
        tmdbId={playing?.tmdbId ?? null}
        mediaType={playing?.type ?? 'movie'}
        season={playing?.season}
        episode={playing?.episode}
        onClose={() => setPlaying(null)}
      />

      <SearchOverlay
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onOpenDetail={handleOpenDetail}
        onPersonOpen={(id) => setPersonId(id)}
      />

      <PersonModal
        personId={personId}
        onClose={() => setPersonId(null)}
        onPlay={handlePlay}
      />

      <MovieDetailModal
        tmdbId={detailMovie}
        onClose={() => setDetailMovie(null)}
        onPlay={handlePlay}
        onPersonOpen={(id) => setPersonId(id)}
        onJumpToAwards={handleJumpToAwards}
      />

      <SeriesDetailModal
        tmdbId={detailSeries}
        onClose={() => setDetailSeries(null)}
        onPlay={handlePlay}
        onPlayEpisode={handlePlayEpisode}
        onPersonOpen={(id) => setPersonId(id)}
        onJumpToAwards={handleJumpToAwards}
      />

      <SettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </div>
  )
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`shrink-0 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? 'bg-accent-primary/10 text-accent-primary'
          : 'text-text-secondary hover:bg-bg-secondary hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  )
}

/**
 * Background-fetches full TMDB details for every entry in a list and
 * exposes them as a `Map<tmdbId, LibraryDetailsEntry>` the filter hook
 * can use for both title search and TMDB-derived sort keys. All calls
 * hit the module-level TMDB cache + concurrency limiter in tmdb.ts,
 * so repeat visits cost ~nothing.
 */
function useLibraryDetailsIndex(
  tmdbIds: number[],
  kind: 'movie' | 'tv',
  apiKey: string | undefined,
  language: string | undefined
): Map<number, LibraryDetailsEntry> {
  const [index, setIndex] = useState<Map<number, LibraryDetailsEntry>>(() => new Map())

  useEffect(() => {
    if (!apiKey || tmdbIds.length === 0) return
    const lang = language || 'en'
    let cancelled = false

    for (const id of tmdbIds) {
      const fetcher =
        kind === 'movie'
          ? fetchMovieDetails(id, apiKey, lang)
          : fetchSeriesDetails(id, apiKey, lang)
      fetcher.then((d) => {
        if (cancelled || !d) return
        const title =
          kind === 'movie'
            ? (d as { title: string }).title
            : (d as { name: string }).name
        if (!title) return
        const entry: LibraryDetailsEntry = {
          title: title.toLowerCase(),
          year: d.year ?? null,
          rating: d.rating ?? 0,
          runtime:
            kind === 'movie' ? ((d as { runtime?: number | null }).runtime ?? null) : null,
          genres: ((d as { genres?: string[] }).genres ?? []),
        }
        setIndex((prev) => {
          const existing = prev.get(id)
          if (
            existing &&
            existing.title === entry.title &&
            existing.year === entry.year &&
            existing.rating === entry.rating &&
            existing.runtime === entry.runtime &&
            existing.genres.length === entry.genres.length &&
            existing.genres.every((g, i) => g === entry.genres[i])
          ) {
            return prev
          }
          const next = new Map(prev)
          next.set(id, entry)
          return next
        })
      })
    }

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tmdbIds.join(','), kind, apiKey, language])

  return index
}
